import * as Api from '@tableau/api-internal-contract-js';
import * as PresLayer from '../../gen-src/preslayer/PresModels';
import { PresLayerOptionalCompatibility } from './PresLayerOptionalCompatibility';
import { PresLayerToSharedApiConverter } from './PresLayerToSharedApiConverter';

/**
 * Class containing helper methods for converting from PresLayerPresModels to their EmbeddingApi PresModels equivalents
 *
 * @export
 * @class PresLayerToEmbeddingApiConverter
 */
export class PresLayerToEmbeddingApiConverter {
  public static convertCustomMarkContextMenu(
    plcustomMarkClickedContextMenuEvent: PresLayer.CustomMarkContextMenuPresModel,
  ): Api.CustomMarkClickedContextMenuEvent {
    const result: Api.CustomMarkClickedContextMenuEvent = {
      visualId: { worksheet: plcustomMarkClickedContextMenuEvent.worksheet },
      contextMenuId: plcustomMarkClickedContextMenuEvent.customMenuItemId,
    };
    return result;
  }

  public static convertFirstVizSizeKnownEvent(
    plfirstVizSizeEvent: PresLayer.EmbeddingVizAndChromeSizePresModel,
  ): Api.FirstVizSizeKnownEvent {
    const sheetSize: Api.SizeConstraint = {
      minHeight: plfirstVizSizeEvent.minHeight,
      maxHeight: plfirstVizSizeEvent.maxHeight,
      minWidth: plfirstVizSizeEvent.minWidth,
      maxWidth: plfirstVizSizeEvent.maxWidth,
    };

    const result: Api.FirstVizSizeKnownEvent = {
      sheetSize: sheetSize,
      chromeHeight: plfirstVizSizeEvent.chromeHeight,
    };

    return result;
  }

  public static convertEmbeddingBootstrap(plEmbeddingBootstrap: PresLayer.EmbeddingApiBootstrapPresModel): Api.EmbeddingBootstrapInfo {
    const worksheetUrls = PresLayerToEmbeddingApiConverter.extractWorksheetUrls(plEmbeddingBootstrap.embeddingSheetsInfo);
    const embeddingBoostrap: Api.EmbeddingBootstrapInfo = {
      workbookName: plEmbeddingBootstrap.workbookName,
      currWorksheetName: plEmbeddingBootstrap.sheetName,
      oldSheetName: plEmbeddingBootstrap.oldSheetName,
      dashboardZones: PresLayerToSharedApiConverter.convertDashboardPresModelToZones(
        plEmbeddingBootstrap.dashboardPresModel,
        worksheetUrls,
      ),
      publishedSheets: plEmbeddingBootstrap.embeddingSheetsInfo.map(PresLayerToEmbeddingApiConverter.convertSheetInfo),
      platformVersion: Api.INTERNAL_CONTRACT_VERSION,
      canDownloadWorkbook: plEmbeddingBootstrap.canDownloadWorkbook,
    };

    // As far as server is in sync with latest api-platform-js, this check isn't needed. Just being safe.
    if (plEmbeddingBootstrap.embeddingStory) {
      embeddingBoostrap.story = PresLayerToEmbeddingApiConverter.convertStoryPresModel(plEmbeddingBootstrap.embeddingStory, worksheetUrls);
    }
    return embeddingBoostrap;
  }

  public static convertStoryPointSwitchEvent(storyPointEvent: PresLayer.EmbeddingStoryPointSwitchPresModel): Api.StoryPointModel {
    const worksheetUrls = PresLayerToEmbeddingApiConverter.extractWorksheetUrls(storyPointEvent.embeddingSheetsInfo);
    return PresLayerToEmbeddingApiConverter.convertStoryPointPresModel(storyPointEvent.embeddingStoryPoint, worksheetUrls);
  }

  private static convertStoryPresModel(storyPm: PresLayer.EmbeddingStoryPresModel, worksheetUrls: Map<string, string>): Api.StoryModel {
    const storyPoints: Array<Api.StoryPointModel> = [];
    storyPm.embeddingStoryPointList.map((storyPointPm) => {
      storyPoints.push(PresLayerToEmbeddingApiConverter.convertStoryPointPresModel(storyPointPm, worksheetUrls));
    });
    return {
      activeStoryPointIndex: storyPm.idx,
      storyPoints: storyPoints,
    };
  }

  public static convertStoryPointPresModel(
    storyPointPm: PresLayer.EmbeddingStoryPointPresModel,
    worksheetUrls?: Map<string, string>,
  ): Api.StoryPointModel {
    const storyPoint: Api.StoryPointModel = {
      caption: storyPointPm.caption,
      index: storyPointPm.idx,
      storyPointId: storyPointPm.storyPointId,
      updated: storyPointPm.isUpdated,
    };

    if (storyPointPm.embeddingStoryContainedSheetInfo && worksheetUrls) {
      storyPoint.containedSheetInfo = {
        name: storyPointPm.embeddingStoryContainedSheetInfo.sheet,
        zoneId: storyPointPm.embeddingStoryContainedSheetInfo.zoneId,
        dashboardZones: PresLayerToSharedApiConverter.convertZoneMapToZones(
          storyPointPm.embeddingStoryContainedSheetInfo.zones,
          worksheetUrls,
        ),
        sheetType: storyPointPm.embeddingStoryContainedSheetInfo.isDashboard ? Api.SheetType.Dashboard : Api.SheetType.Worksheet,
      };
    }
    return storyPoint;
  }

  // tslint:disable-next-line:no-empty
  public static readonly emptyConversion = () => {};

  public static convertWorkbookPublishedAsEvent(
    plWorkbookPublishedAs: PresLayer.WorkbookPublishedAsEventPresModel,
  ): Api.WorkbookPublishedAsModel {
    const workbookPublishedAs: Api.WorkbookPublishedAsModel = {
      newUrl: plWorkbookPublishedAs.workbookPublishedAsUrl,
    };
    return workbookPublishedAs;
  }

  public static convertToolbarStateChanged(plToolbarStateChanged: PresLayer.ToolbarStatePresModel): Api.ToolbarStateEvent {
    const toolbarState: Api.ToolbarState = {
      canRedo: plToolbarStateChanged.canRedo,
      canUndo: plToolbarStateChanged.canUndo,
    };
    const toolbarStateEvent: Api.ToolbarStateEvent = {
      toolbarState: toolbarState,
    };

    return toolbarStateEvent;
  }

  public static convertUrlActionEvent(plUrlAction: PresLayer.URLActionPresModel): Api.UrlActionModel {
    const urlActionModel: Api.UrlActionModel = {
      url: plUrlAction.url,
      target: PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(plUrlAction.target),
    };
    return urlActionModel;
  }

  public static convertSharedFilters(plSharedFilter: PresLayer.SharedFilterPresModel): Api.SharedFilterModel {
    const worksheetInfos: Array<Api.SharedFilterWorksheetModel> = [];
    plSharedFilter.sharedFilterWorksheetInfoList.map((worksheetInfo: PresLayer.SharedFilterWorksheetInfoPresModel) => {
      worksheetInfos.push({
        worksheetName: worksheetInfo.worksheet,
        isActive: worksheetInfo.isActive,
        isEnabled: worksheetInfo.isEnabled,
        isSelected: worksheetInfo.isSelected,
      });
    });
    return { worksheets: worksheetInfos };
  }

  public static convertCustomViewInfo(plCustomViewInfo: PresLayer.EmbeddingCustomViewInfoPresModel): Api.CustomViewInfoModel {
    const customViewInfoModel: Api.CustomViewInfoModel = {
      customViewsList: plCustomViewInfo.embeddingCustomViewList.map(PresLayerToEmbeddingApiConverter.convertCustomView),
    };
    if (plCustomViewInfo.embeddingCustomView) {
      customViewInfoModel.currentView = PresLayerToEmbeddingApiConverter.convertCustomView(plCustomViewInfo.embeddingCustomView);
    }
    return customViewInfoModel;
  }

  public static convertCustomViews(plCustomViews: PresLayer.EmbeddingCustomViewPresModel[]): Api.CustomViewModel[] {
    const customViews: Array<Api.CustomViewModel> = plCustomViews.map(PresLayerToEmbeddingApiConverter.convertCustomView);

    return customViews;
  }

  public static convertCustomView(plCustomView: PresLayer.EmbeddingCustomViewPresModel): Api.CustomViewModel {
    const result: Api.CustomViewModel = {
      id: plCustomView.customViewId,
      name: plCustomView.customViewName,
      urlId: plCustomView.customViewUrlId,
      url: plCustomView.customViewUrl,
      startViewId: plCustomView.customViewStartViewId,
      shared: plCustomView.customViewIsPublic,
      owner: PresLayerToEmbeddingApiConverter.convertUser(plCustomView.embeddingUser),
      luid: plCustomView.customViewLuid,
      isDefault: plCustomView.customViewIsDefault,
    };

    return result;
  }

  private static convertUser(plUser: PresLayer.EmbeddingUserPresModel): Api.UserModel {
    const result: Api.UserModel = {
      userId: plUser.userId,
      userName: plUser.username,
      userFriendlyName: plUser.userFriendlyName,
    };

    return result;
  }

  private static convertSheetInfo(plSheetInfo: PresLayer.EmbeddingSheetInfoPresModel): Api.EmbeddingSheetInfo {
    let sheetType: Api.SheetType;
    if (plSheetInfo.isStory) {
      sheetType = Api.SheetType.Story;
    } else if (plSheetInfo.isDashboard) {
      sheetType = Api.SheetType.Dashboard;
    } else {
      sheetType = Api.SheetType.Worksheet;
    }
    const sheetInfo: Api.EmbeddingSheetInfo = {
      name: plSheetInfo.sheet,
      sheetType: sheetType,
      sizeConstraint: {
        maxHeight: plSheetInfo.maxHeight,
        maxWidth: plSheetInfo.maxWidth,
        minHeight: plSheetInfo.minHeight,
        minWidth: plSheetInfo.minWidth,
      },
      url: plSheetInfo.repositoryUrl,
    };
    return sheetInfo;
  }

  private static extractWorksheetUrls(embeddingSheetsInfo: PresLayer.EmbeddingSheetInfoPresModel[]): Map<string, string> {
    const worksheetUrls = new Map<string, string>();
    embeddingSheetsInfo.forEach((sheetInfo) => {
      if (!sheetInfo.isDashboard && !sheetInfo.isStory) {
        worksheetUrls.set(sheetInfo.sheet, sheetInfo.repositoryUrl);
      }
    });
    return worksheetUrls;
  }

  public static convertAbstractQuery(
    plAbstractQueryPresModel: PresLayer.EmbeddingAbstractQueryWithDataModelPresModel,
  ): Api.EmbeddingAbstractQuery {
    const abstractQueryModel: Api.EmbeddingAbstractQuery = {
      abstractQuery: plAbstractQueryPresModel.embeddingAbstractQuery,
    };

    return abstractQueryModel;
  }
}
